import { createContext, useContext, useEffect, useMemo, useReducer } from "react";

import { ThemeProvider } from "@mui/material/styles";

// Crypto Treenvest themes
import themeRTL from "assets/theme/theme-rtl";

// Crypto Treenvest Dark Mode themes
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// prop-types is a library for typechecking of props
import MDSnackbar from "components/MDSnackbar";
import PropTypes from "prop-types";

// Setting custom name for the context which is visible on react dev tools

// Crypto Treenvest reducer
function reducer(
  state: typeof initialState,
  action: { type: string; value?: any; invest?: typeof invest }
) {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }
    case "WHITE_SIDENAV": {
      return { ...state, whiteSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value };
    }
    case "DIRECTION": {
      return { ...state, direction: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    case "DARKMODE": {
      return { ...state, darkMode: action.value };
    }
    case "SNACKBAR": {
      return { ...state, snackbar: { ...state.snackbar, open: true, ...action.value } };
    }
    case "CLOSE_SNACKBAR": {
      return { ...state, snackbar: { ...state.snackbar, open: false } };
    }
    case "USER_CHANGED": {
      return { ...state, update: true, invest };
    }
    case "NEED_UPDATE": {
      return { ...state, update: true };
    }
    case "START_UPDATE": {
      console.log("Updating...");
      return { ...state, loading: true };
    }
    case "NO_UPDATE": {
      return { ...state, loading: false, update: false };
    }
    case "UPDATE_INVEST": {
      console.log("Updated!");
      return {
        ...state,
        invest: { ...state.invest, ...action.invest },
        update: false,
        loading: false,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}
const invest = {
  loading: true,
  chainId: 0x38,
  user: "0x0000000000000000000000000000000000000000",
  FEE: "0",
  BNBPrice: "0",
  BNBValue: "0",
  userMaxMonth: "0",
  DAILY_REWARD_PERCENT: "200",
  INVEST_BONUS_PERCENT: "10",
  MINIMUM_INVEST: "5000000000",
  REFERRAL_MULTIPLIER: "100000",
  REFERRAL_PERCENT: "200",
  REWARD_MULTIPLIER: "100000",
  REWARD_PERIOD: "15552000",
  userInvestDetails: [
    {
      amount: undefined,
      period: undefined,
      reward: undefined,
      startTime: undefined,
      endTime: undefined,
    },
  ],
  calculateInterest: {
    hourly: undefined,
    referral: undefined,
    gift: undefined,
    requestTime: undefined,
  },
  users: {
    referrer: undefined,
    refAmount: undefined,
    giftAmount: undefined,
    latestWithdraw: undefined,
  },
  // EVENTS
  events: {
    GiftReceived: [],
    ReferralRecieved: [],
  },
  transactions: {
    WithdrawInterest: [],
    WithdrawToInvest: [],
    WithdrawInvest: [],
    UpgradeToMonthly: [],
    Invest6Month: [],
    Invest3Month: [],
    InvestFree: [],
  },
};

const initialState = {
  miniSidenav: false,
  transparentSidenav: false,
  whiteSidenav: false,
  sidenavColor: "success",
  transparentNavbar: true,
  fixedNavbar: true,
  openConfigurator: false,
  direction: "ltr",
  layout: "dashboard",
  darkMode: false,
  update: true,
  snackbar: {
    bgWhite: false,
    open: false,
    icon: "check",
    title: "treenvest",
    content: "test",
    color: "light",
    dateTime: "now",
  },
  invest,
};

const MainContext = createContext([]);

MainContext.displayName = "CryptoTreenvest";

// Crypto Treenvest context provider
const delay = 30;
function MainControllerProvider({ children }) {
  const [controller, dispatch] = useReducer(reducer, initialState);
  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);
  const { snackbar, darkMode } = controller;

  useEffect(() => {
    let timer = setInterval(() => dispatch({ type: "NEED_UPDATE" }), delay * 1000);
    return () => {
      clearInterval(timer);
      dispatch({ type: "NO_UPDATE" });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
      {/* @ts-ignore */}
      <MainContext.Provider value={value}>
        {children}
        <MDSnackbar close={() => setCloseSnackbar(dispatch)} {...snackbar} />
      </MainContext.Provider>
    </ThemeProvider>
  );
}

// Crypto Treenvest custom hook for using context
function useMainController() {
  const context = useContext(MainContext);

  if (!context) {
    throw new Error("useMainController should be used inside the MainControllerProvider.");
  }

  return context;
}

// Typechecking props for the MainControllerProvider
MainControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Context module functions
const setCloseSnackbar = (dispatch) => dispatch({ type: "CLOSE_SNACKBAR" });
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (dispatch, value) => dispatch({ type: "WHITE_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch, value) => dispatch({ type: "DARKMODE", value });

export {
  MainControllerProvider,
  useMainController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
};
